import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Send, Sparkles, ChevronLeft, Menu, Edit2, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';

const AIAssistant = () => {
  // Core chat state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Additional state (keeping for compatibility)
  const [selectedProblem, setSelectedProblem] = useState(null);

  // UI state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [conversationTitle, setConversationTitle] = useState('New Chat');
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false); // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [expandedSources, setExpandedSources] = useState({}); // Track which message sources are expanded

  // Check screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // User state
  const [currentUser, setCurrentUser] = useState(null);

  // Computed: Get current conversation
  const currentConversation = conversations.find(c => c.id === activeConversationId);

  const messagesEndRef = useRef(null);

  // Get API key from environment variable
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const hasApiKey = apiKey && apiKey.trim() !== '' && apiKey !== 'your_actual_gemini_api_key_here';

  // Initialize user authentication
  useEffect(() => {
    const initializeUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw error;

        if (user) {
          setCurrentUser(user);
          await loadConversations(user.id);
        } else {
          toast.error('Please log in to use the AI Assistant');
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        toast.error('Authentication error');
      }
    };

    initializeUser();
  }, []);

  // Load conversations from conversations table
  const loadConversations = async (userId) => {
    try {
      // 1. Fetch chat list from the new 'conversations' table
      const { data, error } = await supabase
        .from('conversations')
        .select('id, title, created_at, is_rag_chat')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // 2. Format them for the state
      const conversationsList = (data || []).map(conv => ({
        id: conv.id,
        title: conv.title || `Chat ${conv.id.slice(0, 8)}...`, // Use saved title
        lastActivity: conv.created_at,
        isRagChat: conv.is_rag_chat || false
      }));

      setConversations(conversationsList);

      // 3. Load first chat or create a new one
      if (conversationsList.length === 0) {
        await createNewConversation();
      } else {
        const mostRecent = conversationsList[0];
        setActiveConversationId(mostRecent.id);
        setConversationTitle(mostRecent.title);
        await loadMessages(mostRecent.id);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Failed to load conversations');
    }
  };

  // Load messages for a conversation from chat_history table
  const loadMessages = async (conversationId) => {
    try {
      const { data, error } = await supabase
        .from('chat_history')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const formattedMessages = (data || []).map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.created_at
      }));

      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    }
  };

  // Create a new conversation
  const createNewConversation = async () => {
    if (!currentUser) return;

    try {
      // 1. Create a new conversation in the 'conversations' table
      const newConversationId = uuidv4();
      const newTitle = 'New Chat';

      const { data, error } = await supabase
        .from('conversations')
        .insert({
          id: newConversationId,
          user_id: currentUser.id,
          title: newTitle,
          is_rag_chat: false
        })
        .select('id, title, created_at, is_rag_chat')
        .single(); // Get the new row back

      if (error) throw error;

      // 2. Add this new conversation to the *top* of the local state
      const newConversation = {
        id: data.id,
        title: data.title,
        lastActivity: data.created_at,
        isRagChat: data.is_rag_chat || false
      };

      setConversations(prev => [newConversation, ...prev]);

      // 3. Set it as active
      setActiveConversationId(newConversation.id);
      setConversationTitle(newConversation.title);
      setMessages([]); // Clear messages for the new chat

      toast.success('New conversation started');
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create new conversation');
    }
  };

  // Create a new RAG conversation
  const createNewRAGChat = async () => {
    if (!currentUser) return;

    try {
      // 1. Create a new RAG conversation in the 'conversations' table
      const newConversationId = uuidv4();
      const newTitle = 'New RAG Chat';

      const { data, error } = await supabase
        .from('conversations')
        .insert({
          id: newConversationId,
          user_id: currentUser.id,
          title: newTitle,
          is_rag_chat: true
        })
        .select('id, title, created_at, is_rag_chat')
        .single();

      if (error) throw error;

      // 2. Add this new conversation to the *top* of the local state
      const newConversation = {
        id: data.id,
        title: data.title,
        lastActivity: data.created_at,
        isRagChat: true
      };

      setConversations(prev => [newConversation, ...prev]);

      // 3. Set it as active
      setActiveConversationId(newConversation.id);
      setConversationTitle(newConversation.title);
      setMessages([]);

      toast.success('New RAG conversation started');
    } catch (error) {
      console.error('Error creating RAG conversation:', error);
      toast.error('Failed to create new RAG conversation');
    }
  };

  // Save message to chat_history table
  const saveMessage = async (conversationId, sender, content) => {
    try {
      const { error } = await supabase
        .from('chat_history')
        .insert([{
          conversation_id: conversationId,
          user_id: currentUser.id,
          sender: sender,
          content: content,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving message:', error);
      throw error;
    }
  };

  // Generate conversation title from first message
  const generateTitle = async (firstMessage) => {
    if (!hasApiKey) return firstMessage.slice(0, 30) + '...';

    try {
      const { GoogleGenerativeAI } = await import('@google/generative-ai');
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

      const prompt = `Generate a short, descriptive title (3-5 words) for a conversation that starts with: "${firstMessage}"`;
      const response = await model.generateContent(prompt);
      const title = response.response.text().trim().replace(/['"]/g, '');

      return title || firstMessage.slice(0, 30) + '...';
    } catch (error) {
      console.error('Error generating title:', error);
      return firstMessage.slice(0, 30) + '...';
    }
  };

  // Generate answer using RAG Edge Function
  const generateRAGAnswer = async (question) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/rag-answer`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: question,
            searchType: 'all',
            matchCount: 7,
            matchThreshold: 0.5,
            includeContext: true
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`RAG API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Debug: Log full response to see what's being returned
      console.log('🔍 RAG Response:', data);
      console.log('📊 Results found:', data.resultsFound);
      console.log('📝 Context preview:', data.context?.substring(0, 500));
      
      // Return answer and sources separately
      return {
        answer: data.answer,
        sources: data.sources || []
      };
    } catch (error) {
      console.error('Error generating RAG answer:', error);
      return {
        answer: "Sorry, I encountered an error using the RAG system. Please try again.",
        sources: []
      };
    }
  };

  // Generate answer using regular AI (existing logic)
  const generateRegularAnswer = async (userInput) => {
    try {
      // Fetch R&D data for context
      const { data: problems, error: problemsError } = await supabase
        .from('problems')
        .select('title, description, status, domain, priority');

      const { data: solutions, error: solutionsError } = await supabase
        .from('solutions')
        .select('description');

      const { data: documents, error: documentsError } = await supabase
        .from('documents')
        .select('category, filename');

      if (problemsError || solutionsError || documentsError) {
        console.error('Database Fetch Error:', problemsError || solutionsError || documentsError);
        throw new Error('Failed to fetch R&D data.');
      }

      // Format the data into a context string
      const contextString = `Here is all the R&D data:

PROBLEMS:
${JSON.stringify(problems, null, 2)}

SOLUTIONS:
${JSON.stringify(solutions?.map(s => s.description) || [], null, 2)}

DOCUMENTS:
${JSON.stringify(documents?.map(d => ({ category: d.category, filename: d.filename })) || [], null, 2)}`;

      // Create the final prompt for the AI
      const finalPrompt = `You are Compass, an AI assistant for the Fubotics R&D team. Your knowledge is strictly limited to the data provided below. Do not invent information. Do not use any external knowledge.

Answer the user's question based *only* on the provided data.

--- R&D DATA ---
${contextString}
--- END OF DATA ---

USER'S QUESTION:
"${userInput}"`;

      // Call the Gemini API
      let aiResponseContent;

      if (hasApiKey) {
        try {
          const { GoogleGenerativeAI } = await import('@google/generative-ai');
          const genAI = new GoogleGenerativeAI(apiKey);
          const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

          const response = await model.generateContent(finalPrompt);
          aiResponseContent = response.response.text();
        } catch (apiError) {
          console.error('Gemini API Error:', apiError);
          aiResponseContent = `(TEST) AI successfully fetched ${problems?.length || 0} problems, ${solutions?.length || 0} solutions, and ${documents?.length || 0} documents. The user asked: ${userInput}. (API call failed, showing test response)`;
        }
      } else {
        aiResponseContent = `(TEST) AI successfully fetched ${problems?.length || 0} problems, ${solutions?.length || 0} solutions, and ${documents?.length || 0} documents. The user asked: ${userInput}. (No API key configured)`;
      }

      return aiResponseContent;
    } catch (error) {
      console.error('Error generating regular answer:', error);
      throw error;
    }
  };

  // --- 5. Handle sending a message ---
  const handleSendMessage = async () => {
    if (!input.trim()) return;

    setIsLoading(true);
    let conversationIdToUse = activeConversationId;

    // --- A. Check for active conversation ---
    if (!conversationIdToUse) {
      toast.error("No active conversation selected.");
      setIsLoading(false);
      return;
    }

    // --- B. Check if this is a RAG chat ---
    const currentConversation = conversations.find(c => c.id === conversationIdToUse);
    const isRagChat = currentConversation?.isRagChat || false;

    const userMessage = {
      sender: 'user',
      content: input.trim(),
      conversation_id: conversationIdToUse,
    };

    // --- C. Add user message to state and save to DB ---
    const userMsg = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');

    await supabase.from('chat_history').insert({
      sender: userMessage.sender,
      content: userMessage.content,
      conversation_id: userMessage.conversation_id,
      // user_id is added by RLS policy/default
    });

    // --- D. Choose AI response method based on chat type ---
    let aiResponseContent;

    try {
      let sources = [];
      
      if (isRagChat) {
        // ===== RAG CHAT: Use RAG-Answer Edge Function =====
        const ragResponse = await generateRAGAnswer(input.trim());
        aiResponseContent = ragResponse.answer;
        sources = ragResponse.sources;
      } else {
        // ===== REGULAR CHAT: Use existing logic =====
        aiResponseContent = await generateRegularAnswer(input.trim());
      }

      // --- E. Save AI message to DB and state ---
      const aiMessage = {
        sender: 'model',
        content: aiResponseContent,
        conversation_id: conversationIdToUse,
      };

      await supabase.from('chat_history').insert({
        sender: aiMessage.sender,
        content: aiMessage.content,
        conversation_id: aiMessage.conversation_id,
      });

      const aiMsg = {
        role: 'assistant',
        content: aiResponseContent,
        sources: sources,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMsg]);

      // --- F. Generate and save title for new conversations ---
      if (messages.length === 0) {
        const title = await generateTitle(input.trim());

        // 1. Save new title to the database
        await supabase
          .from('conversations')
          .update({ title: title })
          .eq('id', conversationIdToUse);

        // 2. Update local state
        setConversationTitle(title);
        setConversations(prev =>
          prev.map(conv =>
            conv.id === conversationIdToUse
              ? { ...conv, title: title, lastActivity: new Date().toISOString() }
              : conv
          )
        );
      } else {
        // Just update last activity time in local state
        setConversations(prev =>
          prev.map(conv =>
            conv.id === conversationIdToUse
              ? { ...conv, lastActivity: new Date().toISOString() }
              : conv
          ).sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity)) // Re-sort list
        );
      }

    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      const errorMsg = {
        role: 'assistant',
        content: isRagChat 
          ? "Sorry, I encountered an error using the RAG system. Please try again."
          : "Sorry, I couldn't fetch the R&D data to answer that.",
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMsg]);
    }

    setIsLoading(false);
  };

  // Handle conversation selection
  const selectConversation = async (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) return;

    setActiveConversationId(conversationId);
    setConversationTitle(conversation.title);
    if (isMobile) setMobileSidebarOpen(false); // Close sidebar on mobile select
    await loadMessages(conversationId);
  };

  // Delete conversation
  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      // 1. Delete from 'conversations' table.
      // RLS policy ensures user can only delete their own.
      // 'ON DELETE CASCADE' will automatically delete all related chat_history.
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      // 2. Update UI
      const updatedConversations = conversations.filter(c => c.id !== conversationId);
      setConversations(updatedConversations);

      // 3. If deleted conversation was active, switch to another or create new
      if (conversationId === activeConversationId) {
        if (updatedConversations.length > 0) {
          await selectConversation(updatedConversations[0].id);
        } else {
          await createNewConversation();
        }
      }

      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  // Update conversation title
  const updateTitle = async (newTitle, conversationId) => {
    if (!newTitle.trim() || !conversationId) {
      setEditingConvId(null);
      return;
    }

    const trimmedTitle = newTitle.trim();

    try {
      // 1. Save the new title to the database
      const { error } = await supabase
        .from('conversations')
        .update({ title: trimmedTitle })
        .eq('id', conversationId);

      if (error) throw error;

      // 2. Update local state
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, title: trimmedTitle }
            : conv
        )
      );

      // 3. Update active title if we edited the active chat
      if (activeConversationId === conversationId) {
        setConversationTitle(trimmedTitle);
      }

      toast.success('Title updated');
    } catch (error) {
      console.error('Error updating title:', error);
      toast.error('Failed to update title');
    } finally {
      // 4. Exit editing mode
      setEditingConvId(null);
      setEditingTitle('');
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full relative overflow-hidden safe-bottom" data-testid="ai-assistant-page">
      {/* Sidebar Backdrop for Mobile */}
      {isMobile && mobileSidebarOpen && (
        <div
          className="absolute inset-0 bg-black/60 z-20"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          ${isMobile ? 'absolute inset-y-0 left-0 z-30 w-[80%] max-w-[300px] shadow-2xl' : sidebarCollapsed ? 'w-[50px]' : 'w-[280px]'} 
          bg-[#1a1f2e] border-r border-white/10 flex flex-col transition-all duration-300
          ${isMobile && !mobileSidebarOpen ? '-translate-x-full' : 'translate-x-0'}
        `}
      >
        {/* Sidebar Header */}
        <div className={`${sidebarCollapsed && !isMobile ? 'p-2' : 'p-4'} border-b border-white/10`}>
          <div className={`flex items-center ${sidebarCollapsed && !isMobile ? 'justify-center' : 'justify-between'}`}>
            {(!sidebarCollapsed || isMobile) && (
              <h3 className="font-semibold text-white">Conversations</h3>
            )}
            {!isMobile && (
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
              >
                {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
              </button>
            )}
            {isMobile && (
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
              >
                <ChevronLeft size={20} />
              </button>
            )}
          </div>

          {(!sidebarCollapsed || isMobile) && (
            <div className="space-y-2">
              <button
                onClick={() => {
                  createNewConversation();
                  if (isMobile) setMobileSidebarOpen(false);
                }}
                className="w-full mt-3 btn-primary flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                New Chat
              </button>
              <button
                onClick={() => {
                  createNewRAGChat();
                  if (isMobile) setMobileSidebarOpen(false);
                }}
                className="w-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 border border-yellow-500/30 text-yellow-400 font-semibold py-2 px-4 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                <Star size={16} className="fill-yellow-400" />
                New RAG Chat
              </button>
            </div>
          )}
        </div>

        {/* Conversations List */}
        {(!sidebarCollapsed || isMobile) && (
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {conversations.map((conversation) => (
              editingConvId === conversation.id ? (
                // --- EDITING VIEW ---
                <div key={conversation.id} className="glass-card p-3 rounded-lg border border-blue-500/50">
                  <input
                    type="text"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') updateTitle(editingTitle, conversation.id);
                      if (e.key === 'Escape') setEditingConvId(null);
                    }}
                    className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-500"
                    autoFocus
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingConvId(null); }}
                      className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); updateTitle(editingTitle, conversation.id); }}
                      className="text-blue-400 hover:text-white text-xs px-2 py-1 rounded bg-blue-500/20 hover:bg-blue-500/40"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                // --- DISPLAY VIEW ---
                <div
                  key={conversation.id}
                  className={`group relative p-3 rounded-lg cursor-pointer transition-colors ${activeConversationId === conversation.id
                      ? 'bg-blue-500/10 border border-blue-500/30'
                      : 'glass-card hover:bg-white/10'
                    }`}
                  onClick={() => selectConversation(conversation.id)}
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-white line-clamp-2 pr-12">
                    {conversation.isRagChat && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Star size={14} className="text-yellow-400 fill-yellow-400" />
                        <span className="text-xs text-yellow-400 font-semibold">RAG</span>
                      </div>
                    )}
                    <span className="line-clamp-2">{conversation.title}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(conversation.lastActivity).toLocaleDateString()}
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingConvId(conversation.id);
                        setEditingTitle(conversation.title);
                      }}
                      className="p-1 rounded hover:bg-white/20 text-gray-400 hover:text-white transition-all"
                      title="Edit title"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conversation.id);
                      }}
                      className="p-1 rounded hover:bg-red-500/20 text-red-400 transition-all"
                      title="Delete chat"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {/* Collapsed Sidebar Actions */}
        {sidebarCollapsed && !isMobile && (
          <div className="flex flex-col items-center p-2 space-y-2">
            <button
              onClick={createNewConversation}
              className="w-10 h-10 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors"
              title="New Chat"
            >
              <Plus size={20} />
            </button>
            <button
              onClick={createNewRAGChat}
              className="w-10 h-10 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg flex items-center justify-center transition-colors"
              title="New RAG Chat"
            >
              <Star size={20} className="text-yellow-400 fill-yellow-400" />
            </button>
          </div>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col w-full min-w-0">
        {/* Chat Header */}
        <div className="p-4 md:p-6 border-b border-white/10 bg-[#1a1f2e]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isMobile && (
                <button
                  onClick={() => setMobileSidebarOpen(true)}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400"
                >
                  <Menu size={20} />
                </button>
              )}
              <div>
                <h2 className="text-xl font-bold text-white">AI Assistant</h2>
                <div className="flex items-center gap-2">
                  {currentConversation?.isRagChat ? (
                    <>
                      <Star size={14} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-sm text-yellow-400 font-semibold">RAG:</span>
                    </>
                  ) : (
                    <Sparkles size={14} className="text-purple-400" />
                  )}
                  <span className="text-sm text-gray-400">{conversationTitle}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-3 lg:p-6 pb-[140px] lg:pb-6">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center">
              <div className="max-w-3xl w-full text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white">How can I help you today?</h3>
                <p className="text-gray-400">Start a conversation by typing a message below.</p>
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles size={20} />
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] p-4 rounded-2xl ${message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'glass-card text-gray-100'
                      }`}
                  >
                    {message.role === 'assistant' ? (
                      <div>
                        <div className="prose prose-sm prose-invert text-gray-100 max-w-none">
                          <ReactMarkdown
                            components={{
                              // Force links to open in a new tab and add security
                              a: ({ node, ...props }) => (
                                <a {...props} target="_blank" rel="noopener noreferrer" />
                              ),
                              // Fix styling for paragraphs inside the markdown
                              p: ({ node, ...props }) => (
                                <p {...props} className="text-gray-100" />
                              ),
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                        
                        {/* Collapsible Sources Section */}
                        {message.sources && message.sources.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-white/10">
                            <button
                              onClick={() => setExpandedSources(prev => ({
                                ...prev,
                                [index]: !prev[index]
                              }))}
                              className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 transition-colors"
                            >
                              {expandedSources[index] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              <span>Sources ({message.sources.length})</span>
                            </button>
                            
                            {expandedSources[index] && (
                              <div className="mt-2 space-y-1">
                                {message.sources.map((source, sIdx) => {
                                  const matchPercent = (source.similarity * 100).toFixed(1);
                                  
                                  return (
                                    <div key={sIdx} className="text-[10px] text-gray-500 leading-relaxed">
                                      • [{source.type.toUpperCase()}] {matchPercent}% match ({source.content || 'No content available'})
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles size={20} />
                  </div>
                  <div className="glass-card p-4 rounded-2xl">
                    <div className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="mobile-chat-input">
          <div className="max-w-4xl mx-auto">
            <div className="glass-card p-2 flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={hasApiKey ? "Type your message..." : "Gemini API key not configured..."}
                className="flex-1 bg-transparent border-none outline-none resize-none text-sm px-2 py-2 text-white placeholder-gray-400"
                rows="1"
                style={{ maxHeight: '120px' }}
                disabled={!hasApiKey || isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!input.trim() || isLoading || !hasApiKey}
                className="w-10 h-10 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;